import { useState } from 'react';
import cn from 'classnames';

import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  useRealtyClaimMutation,
  useUpdateRealtyMutation,
} from '../../../../redux/services/realties/realtiesApi';
import { selectUserToken } from '../../../../redux/features/user/userSelectors';

import { ContactsButtons } from '../ContactsButtons';
import { CardInputsBlock } from '../../../form/forms/realtyCardForms/CardInputsBlock';
import { RealtyStatus } from '../RealtyStatus';
import { SquareButtons } from '../SquareButtons';
import { RoundedButtons } from '../RoundedButtons';
import { HistoryButton } from '../HistoryButton';
import { InfoButtons } from '../InfoButtons';
import { LocationButtons } from '../LocationButtons';
import { DetailsRealty } from '../DetailsRealty';
import { CharacteristicsRealty } from '../CharacteristicsRealty';
import { OwnersRealty } from '../OwnersRealty';
import { CardRealtyPhotos } from '../CardRealtyPhotos';

import { Loader } from '../../Loader';
import { ButtonTemplate } from '../../buttons/ButtonTemplate';

import { showError } from '../../../../utils/ui';
import { handleResponseError } from '../../../../utils/data';

export const CardRealty = ({ realty = {}, isPwa = false }) => {
  const [isLikeLoading, setIsLikeLoading] = useState(false);

  const {
    main,
    agent,
    id,
    tariff,
    urgency_type,
    location,
    contacts,
    created_at,
    updated_at,
    photo = {},
    likes,
  } = realty;
  const { realty_status, realty_stages, external_link } = main ?? {};
  const tariff_ttc = tariff?.tariff_ttc;
  const { photos = [] } = photo;

  const userToken = useSelector(selectUserToken);
  const canEdit = !!realty?.can?.update;
  const canClaim = !!realty?.can?.claim;
  const canRelease = !!realty?.can?.release;
  const [updateRealty] = useUpdateRealtyMutation();
  const [realtyClaim, { isLoading: isClaimLoading }] = useRealtyClaimMutation();

  const handleClaimAction = async (action, successText) => {
    const response = await realtyClaim({ token: userToken, id, action });

    if (response?.error) {
      showError(handleResponseError(response.error));
      return;
    }

    toast.success(successText);
  };

  const updateRealtyData = async values => {
    const response = await updateRealty({ token: userToken, data: values, id });

    if (response?.error) {
      showError(handleResponseError(response.error));

      return false;
    }

    return true;
  };

  return (
    <div
      className={cn(
        'flex flex-col w-full border-[1px] border-gray-200 shadow',
        { 'xs:w-[360px]': !isPwa }
      )}
    >
      <div
        className={cn('relative w-full h-[240px]', {
          'xs:w-[360px]': !isPwa,
        })}
      >
        <CardRealtyPhotos
          realtyId={id}
          photos={photos}
          isPwa={isPwa}
        />

        <RealtyStatus
          updateRealtyData={updateRealtyData}
          realtyStatus={realty_status}
          canEdit={canEdit}
        />

        <SquareButtons
          realtyId={id}
          hasPhotos={photos.length > 0}
          externalLink={external_link}
          canEdit={canEdit}
        />

        <RoundedButtons
          realtyId={id}
          likes={likes}
          realty={realty}
          onLikeLoadingChange={setIsLikeLoading}
        />

        <HistoryButton realtyId={id} />

        {isLikeLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
            <Loader
              width={32}
              height={32}
            />
          </div>
        )}

        <ContactsButtons
          agent={agent}
          realtyId={id}
          ttc={tariff_ttc}
        />
      </div>

      <div className="flex flex-col items-start gap-3 pt-3.5 pb-3 px-2 w-full">
        {canClaim && (
          <ButtonTemplate
            text="Take this listing"
            handleClick={() => handleClaimAction('claim', 'Listing is now yours')}
            isLoadingData={isClaimLoading}
            isSmall={true}
            isSmallBorder={true}
            tooltipText="Imported listing: take it over to add mandates and documents"
          />
        )}

        {canRelease && (
          <ButtonTemplate
            text="Return to import pool"
            handleClick={() =>
              handleClaimAction('release', 'Listing returned to the import pool')
            }
            isLoadingData={isClaimLoading}
            isSmall={true}
            isSmallBorder={true}
            tooltipText="Give the listing back; your mandates and documents stay hidden from the next agent"
          />
        )}

        <div
          className={cn('w-full', { 'pointer-events-none': !canEdit })}
          inert={canEdit ? undefined : ''}
        >
          <CardInputsBlock
            updateRealtyData={updateRealtyData}
            realtyId={id}
            urgencyType={urgency_type}
            realtyStages={realty_stages}
          />
        </div>

        <InfoButtons
          realty={realty}
          canEdit={canEdit}
        />

        <LocationButtons
          updateRealtyData={updateRealtyData}
          canEdit={canEdit}
          realtyId={id}
          realtyLocation={location}
        />

        <DetailsRealty
          realty={realty}
          canEdit={canEdit}
        />

        <CharacteristicsRealty
          realty={realty}
          canEdit={canEdit}
        />

        <OwnersRealty
          realty={realty}
          canEdit={canEdit}
          contacts={contacts}
        />

        <div>
          <span className="text-xs text-gray-600">{`Created at: ${created_at}${updated_at ? ` / Updated at: ${updated_at}` : ''}`}</span>
        </div>
      </div>
    </div>
  );
};
